import { useState } from 'react';
import type { PresenceStatus, PublicUser } from '@clanfire/shared';
import { AvatarDecoration } from '@/components/profile/AvatarDecoration';
import { cn, colorFor, contrastText, initials } from '@/lib/utils';

const statusColor: Record<PresenceStatus, string> = {
  online: 'bg-online',
  idle: 'bg-idle',
  dnd: 'bg-dnd',
  offline: 'bg-offline',
};

export const statusLabel: Record<PresenceStatus, string> = {
  online: 'Online',
  idle: 'Idle',
  dnd: 'Do Not Disturb',
  offline: 'Offline',
};

export function StatusDot({ status, className, ring = 'ring-sidebar' }: { status: PresenceStatus; className?: string; ring?: string }) {
  return (
    <span
      aria-label={statusLabel[status]}
      title={statusLabel[status]}
      className={cn('block rounded-full ring-[3px]', ring, statusColor[status], className)}
    >
      {status === 'dnd' && <span className="mx-auto mt-[40%] block h-[20%] w-[55%] rounded-full bg-black/45" />}
      {status === 'offline' && <span className="m-[25%] block h-1/2 w-1/2 rounded-full bg-black/45" />}
    </span>
  );
}

interface AvatarProps {
  user: (Pick<PublicUser, 'id' | 'displayName' | 'avatarUrl'> & Partial<Pick<PublicUser, 'avatarDecoration'>>) | null;
  size?: number;
  /** Draw the user's avatar decoration (default: when the avatar is at least 32px). */
  decorate?: boolean;
  status?: PresenceStatus;
  speaking?: boolean;
  className?: string;
  ring?: string;
}

/** "Emberwood" -> "Em", "Pixel Guild" -> "PG". */
function serverInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  const chars = [...(words[0] ?? '?')];
  return chars[0]!.toUpperCase() + (chars[1] ?? '').toLowerCase();
}

/** User avatar with a generated initials fallback and optional presence dot. */
export function Avatar({ user, size = 40, status, speaking, className, ring, decorate }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const name = user?.displayName ?? 'Deleted User';
  const bg = user ? colorFor(user.id) : '#6b6478';
  const dot = Math.max(10, Math.round(size * 0.32));

  return (
    // Decorative: every avatar sits next to (or inside a control labelled with) the user's name.
    <span aria-hidden={!status || undefined} className={cn('relative inline-flex shrink-0', className)} style={{ width: size, height: size }}>
      {user?.avatarUrl && !failed ? (
        <img
          src={user.avatarUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className={cn('h-full w-full rounded-full object-cover transition-shadow', speaking && 'ring-[3px] ring-online')}
          draggable={false}
        />
      ) : (
        <span
          aria-hidden
          className={cn('flex h-full w-full items-center justify-center rounded-full font-bold transition-shadow select-none', speaking && 'ring-[3px] ring-online')}
          style={{ background: bg, color: contrastText(bg), fontSize: Math.max(10, size * 0.38) }}
        >
          {initials(name)}
        </span>
      )}
      {user?.avatarDecoration && (decorate ?? size >= 32) && <AvatarDecoration id={user.avatarDecoration} size={size} />}
      {status && (
        <span className="absolute -right-0.5 -bottom-0.5" style={{ width: dot, height: dot }}>
          <StatusDot status={status} ring={ring} className="h-full w-full" />
        </span>
      )}
    </span>
  );
}

/** A group DM's picture: its first two members' avatars, overlapping (or one, for a group of two). */
export function GroupAvatar({ users, size = 40, className, ring = 'ring-sidebar' }: { users: AvatarProps['user'][]; size?: number; className?: string; ring?: string }) {
  const [first, second] = users;
  if (!second) return <Avatar user={first ?? null} size={size} decorate={false} className={className} />;
  const inner = Math.round(size * 0.68);
  return (
    <span aria-hidden className={cn('relative inline-flex shrink-0', className)} style={{ width: size, height: size }}>
      <Avatar user={second} size={inner} decorate={false} className="absolute top-0 right-0" />
      <Avatar user={first ?? null} size={inner} decorate={false} className={cn('absolute bottom-0 left-0 rounded-full ring-2', ring)} />
    </span>
  );
}

export function ServerIcon({
  server,
  size = 48,
  className,
}: {
  server: { id: string; name: string; iconUrl: string | null };
  size?: number;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const bg = colorFor(server.id);
  if (server.iconUrl && !failed) {
    return (
      <img
        src={server.iconUrl}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className={cn('shrink-0 rounded-full object-cover', className)}
        style={{ width: size, height: size }}
        draggable={false}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn('flex shrink-0 items-center justify-center rounded-full font-display select-none', className)}
      style={{ width: size, height: size, background: bg, color: contrastText(bg), fontSize: size * 0.36 }}
    >
      {serverInitials(server.name)}
    </span>
  );
}

export function CountBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={`${count} unread`}
      className={cn('inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1.5 text-[11px] leading-none font-bold text-accent-fg', className)}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
